<template>
  <div class="preview-material">
    <div class="preview-error-container" v-if="previewError">
      <pn-toast icon="alert-exclamation-circle" appearance="warning">
        <p v-html="$t('W2P_PREVIEW_ERROR_MESSAGE')"></p>
      </pn-toast>
      <div class="icon-container">
        <p v-html="$t('W2P_LETTERS_RESET')"></p>
        <pn-icon
          :aria-label="$t('W2P_ARIA_LABEL_REMOVE_PDF_BTN')"
          symbol="trash-can"
          size="medium"
          color="blue700"
          solid
          @keydown.enter="removeFile"
          @click="removeFile"
        ></pn-icon>
      </div>
    </div>

    <div v-if="!previewError">
      <div class="loading-preview" v-if="loadingPreview">
        <pn-icon
          symbol="spinner-blue"
          size="medium"
          color="blue700"
          solid
        ></pn-icon>
      </div>

      <pn-toast
        icon="alert-exclamation-circle"
        appearance="warning"
        v-if="showWarnings && !loadingPreview && getResultWarnings.length"
      >
        <p v-html="$t('W2P_PREVIEW_WARNING_MESSAGE')"></p>
      </pn-toast>

      <div
        :class="['controllers', { single: showControllers === 1 }]"
        v-show="showControllers && !loadingPreview"
      >
        <div class="control mobile-hide" v-if="showScale">
          <pn-icon
            tabindex="0"
            :aria-label="$t('W2P_ARIA_LABEL_ZOOM_OUT_BTN')"
            symbol="minus"
            size="medium"
            color="blue700"
            solid
            @keydown.enter="zoomIn"
            @click="zoomIn"
          ></pn-icon>
          {{ formattedZoom }} %
          <pn-icon
            tabindex="0"
            :aria-label="$t('W2P_ARIA_LABEL_ZOOM_IN_BTN')"
            symbol="plus"
            size="medium"
            color="blue700"
            solid
            @keydown.enter="zoomOut"
            @click="zoomOut"
          ></pn-icon>
        </div>

        <div class="control" v-if="showPagination">
          <pn-icon
            tabindex="0"
            :aria-label="$t('W2P_ARIA_LABEL_PREVIOUS_PAGE_BTN')"
            symbol="angle-small-left"
            size="medium"
            color="blue700"
            solid
            v-show="numPages > 1 && page > 1"
            @keydown.enter="setCurrentPage(--page)"
            @click="setCurrentPage(--page)"
          ></pn-icon>
          {{ $t('W2P_PREVIEW_PAGES_NAME') }} {{ page }}/{{ numPages }}
          <pn-icon
            tabindex="0"
            :aria-label="$t('W2P_ARIA_LABEL_NEXT_PAGE_BTN')"
            symbol="angle-small-right"
            size="medium"
            color="blue700"
            solid
            v-show="numPages > 1 && page < numPages"
            @keydown.enter="setCurrentPage(++page)"
            @click="setCurrentPage(++page)"
          ></pn-icon>
        </div>

        <div class="control" v-if="showDeleteButton">
          <pn-icon
            tabindex="0"
            :aria-label="$t('W2P_ARIA_LABEL_REMOVE_PDF_BTN')"
            symbol="trash-can"
            size="medium"
            color="blue700"
            solid
            v-if="showDeleteButton"
            @keydown.enter="removeFile"
            @click="removeFile"
          ></pn-icon>
        </div>
      </div>
      <div id="pdfvuer" v-show="!loadingPreview" class="pdf-view-container">
        <CustomResponsiveTooltip
          :solid="true"
          :color="'orange400'"
          :hoverColor="'orange900'"
          :position="'left'"
          :warning="true"
          :className="'font-format-warning'"
          v-if="showWarnings && hasDisallowedFontsWarning"
        >
          <strong>{{
            $t('W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_FONTS_TITLE')
          }}</strong>
          <p
            class="warning-message"
            v-html="
              $t('W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_FONTS_MSG', {
                fonts: getInvalidFontsTranslate(getDisallowedFonts)
              })
            "
          ></p>
        </CustomResponsiveTooltip>

        <CustomResponsiveTooltip
          :solid="true"
          :color="'orange400'"
          :hoverColor="'orange900'"
          :position="'left'"
          :warning="true"
          :className="'low-reso-warning'"
          v-if="showWarnings && hasLowImageResoWarning"
        >
          <strong>{{
            $t('W2P_UPLOAD_MATERIAL_WARN_IMAGE_RESOLUTION_TITLE')
          }}</strong>
          <p class="warning-message">
            {{ $t('W2P_UPLOAD_MATERIAL_WARN_IMAGE_RESOLUTION_MSG') }}
          </p>
        </CustomResponsiveTooltip>

        <CustomResponsiveTooltip
          :solid="true"
          :color="'orange400'"
          :hoverColor="'orange900'"
          :position="'left'"
          :warning="true"
          :className="'disallowed-elements-warning'"
          v-if="showWarnings && hasDisallowedElementsWarning"
        >
          <strong>{{
            $t('W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_ELEMENTS_TITLE')
          }}</strong>
          <p class="warning-message">
            {{ $t('W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_ELEMENTS_MSG') }}
          </p>
        </CustomResponsiveTooltip>

        <div
          aria-hidden="true"
          class="parent-viewer-container"
          :style="{
            'overflow-x': scale > 1 ? 'scroll' : 'hidden',
            'overflow-y': 'hidden',
            width: '100%'
          }"
        >
          <div class="parent-viewer">
            <div class="preview-holder">
              <div class="pdf-viewer">
                <pdf
                  :src="pdfdata"
                  v-for="i in numPages"
                  :key="i"
                  :id="i"
                  :page="i"
                  :resize="true"
                  style="width:100%;"
                >
                </pdf>
              </div>
            </div>
          </div>
        </div>
        <CustomResponsiveTooltip
          :solid="true"
          :color="'orange400'"
          :hoverColor="'orange900'"
          :position="'bottom'"
          :warning="true"
          :className="'transparent-image-warning'"
          v-if="showWarnings && hasTransparentImagesWarning"
        >
          <strong>{{
            $t('W2P_UPLOAD_MATERIAL_WARN_IMAGE_TRANSPARENT_TITLE')
          }}</strong>
          <p class="warning-message">
            {{ $t('W2P_UPLOAD_MATERIAL_WARN_IMAGE_TRANSPARENT_MSG') }}
          </p>
        </CustomResponsiveTooltip>
      </div>
    </div>
  </div>
</template>

<script>
import pdfvuer from 'pdfvuer'
import { setTimeout } from 'timers'
import { mapMutations, mapState, mapGetters } from 'vuex'
import * as uploadMaterialHelper from '@/helpers/uploadMaterialHelper'
import PdfViewer from '@/components/PdfViewerComponent'
import CustomResponsiveTooltip from '@/components/common/CustomResponsiveTooltip'

export default {
  name: 'PreviewMaterialComponent',
  props: {
    controls: {
      type: Array,
      default: () => {
        return []
      }
    },
    showWarnings: {
      type: Boolean,
      default: false
    },
    src: {
      type: String,
      default: null
    }
  },
  components: {
    pdf: PdfViewer,
    CustomResponsiveTooltip
  },
  data() {
    return {
      page: 1,
      numPages: 0,
      pdfdata: undefined,
      scale: 1,
      loadingPreview: true,
      previewError: false
    }
  },
  computed: {
    ...mapState({
      upload: state => state.letter.upload
    }),
    ...mapGetters('letter/upload', [
      'getResultWarnings',
      'hasTransparentImagesWarning',
      'hasLowImageResoWarning',
      'hasDisallowedElementsWarning',
      'hasDisallowedFontsWarning',
      'getDisallowedFonts'
    ]),
    formattedZoom() {
      return Number.parseInt(this.scale * 100)
    },
    showControllers() {
      return this.controls.length
    },
    showScale() {
      return this.controls.includes('scale')
    },
    showPagination() {
      return this.controls.includes('pagination')
    },
    showDeleteButton() {
      return this.controls.includes('delete')
    }
  },
  mounted() {
    if (this.src) {
      this.loadPdf(this.src)
    } else {
      if (this.upload.previewUrl) {
        this.loadPdf(this.upload.previewUrl)
      } else {
        uploadMaterialHelper
          .generatePreview('letter')
          .then(url => {
            this.setMaterial({
              key: 'previewUrl',
              value: url
            })

            this.loadPdf(url)
          })
          .catch(() => {
            this.previewError = true
            this.setMaterial({
              key: 'previewUrl',
              value: ''
            })
          })
      }
    }

    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
  },
  watch: {
    scale: function(v) {
      document.getElementsByClassName('parent-viewer')[0].style['width'] =
        v * 100 + '%'

      setTimeout(() => {
        this.onWindowResize()
      }, 10)
    }
  },
  methods: {
    ...mapMutations({
      toogleApproveMaterial(commit, payload) {
        return commit('letter/upload/TOOGLE_MATERIAL_APPROVE_PRINTING', payload)
      },
      resetMaterial(commit) {
        return commit('letter/upload/RESET_MATERIAL')
      },
      setMaterial(commit, payload) {
        return commit('letter/upload/SET_MATERIAL', payload)
      },
      resetAddressState(commit) {
        return commit('letter/address/RESET_STATE')
      }
    }),
    onWindowResize() {
      this.setPageViewerHeight()
      this.setCurrentPage(this.page)
    },
    zoomIn() {
      this.scale -= this.scale > 0.2 ? 0.1 : 0
    },
    zoomOut() {
      this.scale += this.scale < 2 ? 0.1 : 0
    },
    setCurrentPage(p) {
      setTimeout(() => {
        const target = document.getElementById(p)

        if (target) {
          const targetParent = target.parentNode
          targetParent.parentNode.scroll(0, target.clientHeight * (p - 1))
        }
      }, 10)
    },
    loadPdf(url) {
      var self = this
      self.pdfdata = pdfvuer.createLoadingTask(url)
      self.pdfdata
        .then(pdf => {
          self.numPages = pdf.numPages

          setTimeout(() => {
            self.setPageViewerHeight()
          }, 500)

          self.loadingPreview = false
          self.$emit('loading', false)
        })
        .catch(() => {
          self.previewError = true
          this.setMaterial({
            key: 'previewUrl',
            value: ''
          })
        })
    },
    findPos(obj) {
      return obj.offsetTop
    },
    setPageViewerHeight() {
      const page = document.getElementsByClassName('textLayer')[0]
      const parent = document.getElementsByClassName('parent-viewer')[0]
      const viewer = document.getElementsByClassName('preview-holder')[0]

      if (page) {
        parent.style['height'] = viewer.style[
          'height'
        ] = `${page.clientHeight}px`
      }
    },
    removeFile() {
      this.resetMaterial()
      this.resetAddressState()
      this.back()
    },
    getInvalidFontsTranslate(invalidFonts) {
      const fonts = invalidFonts.map(x => `<b>${x.label.options.font}</b>`)

      let translate = fonts[0]

      if (fonts.length > 1) {
        const last = fonts.pop()

        // Spaces here are needed to follow the breakline of the tooltip width
        translate = fonts.join('<b>,</b> &nbsp;')
        translate += ' &nbsp;' + this.$t('W2P_AND') + ' &nbsp;'
        translate += last + ' &nbsp;'
      }

      return translate
    },
    back() {
      this.$router.push('/letter/upload/upload-letter')
    }
  }
}
</script>

<style lang="scss" scoped>
.pdf-viewer {
  position: relative;
}

.preview-error-container {
  display: flex;
  align-items: center;

  pn-toast {
    width: 100%;
  }

  .icon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-left: 1em;
    cursor: pointer;
  }
}

.preview-material {
  font-size: 1.6rem;
  width: 100%;

  pn-toast {
    margin-top: 1.5em;
  }

  .loading-preview {
    padding: 2em;
    text-align: center;
  }
  .controllers {
    display: flex;
    justify-content: space-between;
    margin: 2em 0;

    &.single {
      justify-content: center;
    }

    .control {
      line-height: 1.5em;

      pn-icon {
        &:hover {
          cursor: pointer;
        }
      }

      &:last-child {
        pn-icon:nth-child(even) {
          margin-left: 1em;
        }
      }
    }
  }
  #pdfvuer {
    position: relative;

    pn-help-tooltip {
      z-index: 2;
      padding: 2px;
      background-color: $white;
      border-radius: 50%;
      position: absolute;

      &.transparent-image-warning {
        top: -1em;
        left: 50%;
      }

      &.font-format-warning {
        top: 40%;
        right: -0.8em;
      }

      &.low-reso-warning {
        top: 50%;
        right: -0.8em;
      }

      &.disallowed-elements-warning {
        top: 60%;
        right: -0.8em;
      }

      .warning-message {
        margin-top: 0.5em;
      }
    }

    .parent-viewer-container {
      /* Scrollbar width */
      &::-webkit-scrollbar {
        background-color: $white;
        width: 0.75em;
      }

      /* Scrollbar button */
      &::-webkit-scrollbar-button {
        display: none;
      }

      /* Track */
      &::-webkit-scrollbar-track {
        background-color: $white;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background-color: $gray400;
        border-radius: 1em;
        border: 4px solid $white;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: $gray700;
      }

      -webkit-box-shadow: 0px 8px 24px -7px rgba(0, 0, 0, 0.55);
      -moz-box-shadow: 0px 8px 24px -7px rgba(0, 0, 0, 0.55);
      box-shadow: 0px 8px 24px -7px rgba(0, 0, 0, 0.55);
    }

    .parent-viewer {
      display: block;
      position: relative;
      margin: 0 auto;

      .preview-holder {
        position: absolute;
        z-index: 1;
        width: 100%;
        overflow: hidden;
      }
    }
  }
}

@media screen and (max-width: 50em) {
  .controllers {
    .control.mobile-hide {
      display: none;
    }
  }
}
</style>
